import { permissionsHeld, type Actor, type MeResponse, type Permission } from '@hrms/shared';

/**
 * The Actor and permission set the web app derives from a `/me` response. Pure, so it is tested
 * without React.
 *
 * `rolePermissions` is what each held role means in THIS organisation. Without it `can()` falls
 * back to the built-in role table, which knows nothing about a role an admin created — a person
 * holding one would see an empty menu — and shows code defaults for a built-in role the org has
 * edited.
 *
 * `readOnly` (ended trial, billing lock) goes on the Actor so `can()` hides every write action the
 * API would refuse; reads and billing/agreement stay allowed. Recomputing from the map uses it; the
 * server-computed list already reflects it.
 *
 * Against an older API that does not send the map, `permissions` (which the server always computed
 * correctly) is the better source than recomputing from the code defaults.
 */
export function actorFromMe(body: MeResponse): {
  actor: Actor;
  permissions: ReadonlySet<Permission>;
} {
  const actor: Actor = {
    personId: body.person.id,
    orgId: body.person.orgId,
    grants: body.grants,
    impersonatorPersonId: body.session.impersonatedBy,
    ...(body.rolePermissions ? { rolePermissions: body.rolePermissions } : {}),
    ...(body.readOnly ? { readOnly: true } : {}),
  };
  const permissions: ReadonlySet<Permission> = body.rolePermissions
    ? permissionsHeld(actor)
    : new Set(body.permissions);
  return { actor, permissions };
}
